// Borde HTTP de /users contra Core Manage API v1.
//
// La API pública de este repositorio NO cambia respecto al legado: `application/` y `presentation/`
// quedan intactos (spec.md §1). Las diferencias del contrato v1 se absorben aquí:
//   - `/users` pagina (`{items,…}`), el resto de listados de v1 no (response-shapes.md).
//   - El DTO ya no trae `roleName`: se resuelve cruzando `roleIds` con `GET /roles`.
//   - Borrar un usuario pasa a ser baja lógica (`POST /users/{id}/deactivate`, spec.md C-1).
//   - Los tres métodos de rol se implementan sobre `PUT /users/{id}/roles`, que reemplaza el
//     conjunto completo (spec.md C-2); el delta lo calcula este repositorio.

import { z } from 'zod';
import apiClient from '@shared/api/http';
import {
  PaginatedUsers,
  UserDetailsDto,
  UserSummaryDto,
} from '@shared/api/generated/contract';
import type { ClientId, PlatformId, RoleId, UserId } from '../domain/ids';
import type {
  User,
  UserCreatePayload,
  UserRole,
  UserType,
  UserUpdatePayload,
} from '../domain/user';

type SummaryDto = z.infer<typeof UserSummaryDto>;
type DetailsDto = z.infer<typeof UserDetailsDto>;

const RoleNameDtoSchema = z.object({ id: z.string(), name: z.string() });

// v1 llama `internal` a lo que el dominio llama `employee`; el resto de valores coincide.
function toUserType(value: string): UserType {
  return value === 'internal' ? 'employee' : (value as UserType);
}

/**
 * Campos que v1 dejó de exponer en los DTO de usuario: `phone`, país, idioma, empresa, DU/PU,
 * plataformas de rol y standby embebido. Se devuelven ausentes en vez de inventarlos; las pantallas
 * que los mostraban quedan en blanco. Registrado como G-15 en improvements.md.
 */
function toDomain(dto: SummaryDto | DetailsDto, roleNames: ReadonlyMap<string, string>): User {
  const roleIds = dto.roleIds ?? [];
  const primaryRoleId = roleIds[0];
  const detail = 'roles' in dto ? dto : undefined;

  return {
    id: dto.id as UserId,
    userType: toUserType(dto.userType),
    // v1 no devuelve `username` en los DTO de lectura aunque lo acepta al crear (G-15).
    username: dto.email,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    roleId: primaryRoleId as RoleId,
    roleName: (primaryRoleId && roleNames.get(primaryRoleId)) ?? '',
    clientId: dto.clientIds?.[0] as ClientId | undefined,
    userRoles: detail?.roles?.map(
      (role, index): UserRole => ({
        id: role.id as RoleId,
        roleName: role.name,
        isPrimary: index === 0,
      })
    ),
    clientAssignments: detail?.clientAssignments?.map((assignment) => ({
      clientId: assignment.clientId as ClientId,
      clientName: '',
      assignedPercentage: assignment.dedicationPercentage,
    })),
  };
}

// El listado solo trae `roleIds`; los nombres viven en `/roles` (array desnudo, 5 elementos).
async function fetchRoleNames(): Promise<ReadonlyMap<string, string>> {
  const { data } = await apiClient.get('/roles');
  const roles = z.array(RoleNameDtoSchema.passthrough()).parse(data);
  return new Map(roles.map((role) => [role.id, role.name]));
}

function toCreateDto(payload: UserCreatePayload) {
  return {
    userType: payload.userType === 'employee' ? 'internal' : payload.userType,
    username: payload.username,
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    roleIds: payload.roleId ? [payload.roleId] : [],
    linkedClientId: payload.clientId,
    clientAssignments: payload.assignmentClientIds?.map((clientId) => ({ clientId })),
  };
}

function toUpdateDto(payload: UserUpdatePayload) {
  return {
    userType: payload.userType === 'employee' ? 'internal' : payload.userType,
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    linkedClientId: payload.clientId,
  };
}

async function readUserRoles(id: UserId): Promise<UserRole[]> {
  const { data } = await apiClient.get(`/users/${id}`);
  const detail = UserDetailsDto.parse(data);
  return (detail.roles ?? []).map((role, index) => ({
    id: role.id as RoleId,
    roleName: role.name,
    isPrimary: index === 0,
  }));
}

// `PUT /users/{id}/roles` sustituye el conjunto completo. El contrato declara el cuerpo como
// `JsonObject` opaco; `roleIds` se infiere de `CreateUserDto` y queda pendiente de confirmar en el
// smoke test (spec.md §6, G-10).
async function replaceUserRoles(id: UserId, roles: UserRole[]): Promise<UserRole[]> {
  await apiClient.put(`/users/${id}/roles`, { roleIds: roles.map((role) => role.id) });
  return roles;
}

export interface GetUsersByPlatformParams {
  clientId?: ClientId;
  roleId?: RoleId;
  platformCategoryId?: string | number;
}

export const userRepository = {
  async getAll(): Promise<User[]> {
    const [{ data }, roleNames] = await Promise.all([apiClient.get('/users'), fetchRoleNames()]);
    return PaginatedUsers.parse(data).items.map((dto) => toDomain(dto, roleNames));
  },

  async getById(id: UserId): Promise<User | null> {
    const [{ data }, roleNames] = await Promise.all([
      apiClient.get(`/users/${id}`),
      fetchRoleNames(),
    ]);
    if (!data) return null;
    return toDomain(UserDetailsDto.parse(data), roleNames);
  },

  async create(payload: UserCreatePayload): Promise<User> {
    const { data } = await apiClient.post('/users', toCreateDto(payload));
    return toDomain(UserDetailsDto.parse(data), await fetchRoleNames());
  },

  async update(id: UserId, payload: UserUpdatePayload): Promise<User> {
    const { data } = await apiClient.put(`/users/${id}`, toUpdateDto(payload));
    return toDomain(UserDetailsDto.parse(data), await fetchRoleNames());
  },

  // C-1: v1 sustituyó el borrado por baja lógica. El usuario sigue listable con `status`
  // `deactivated` y puede reactivarse con `POST /users/{id}/reactivate`.
  async suspend(id: UserId): Promise<void> {
    await apiClient.post(`/users/${id}/deactivate`);
  },

  async addRole(id: UserId, roleId: RoleId): Promise<UserRole[]> {
    const current = await readUserRoles(id);
    if (current.some((role) => role.id === roleId)) return current;
    return replaceUserRoles(id, [...current, { id: roleId, roleName: '', isPrimary: false }]);
  },

  async removeRole(id: UserId, roleId: RoleId): Promise<UserRole[]> {
    const current = await readUserRoles(id);
    return replaceUserRoles(
      id,
      current.filter((role) => role.id !== roleId)
    );
  },

  // v1 no marca rol primario: se representa poniéndolo primero en el conjunto enviado.
  async setPrimaryRole(id: UserId, roleId: RoleId): Promise<UserRole[]> {
    const current = await readUserRoles(id);
    const promoted = current.find((role) => role.id === roleId);
    if (!promoted) return current;
    const reordered = [promoted, ...current.filter((role) => role.id !== roleId)];
    return replaceUserRoles(
      id,
      reordered.map((role, index) => ({ ...role, isPrimary: index === 0 }))
    );
  },

  // Conservado por consumo público (§6/R-5): change-mgmt/clients/workforce lo esperan de `iam`.
  async getByPlatform(
    platformId: PlatformId,
    params: GetUsersByPlatformParams = {}
  ): Promise<User[]> {
    const query: Record<string, string | number> = {};
    if (params.clientId) query.clientId = params.clientId;
    if (params.roleId) query.roleId = params.roleId;
    if (params.platformCategoryId) query.platformCategoryId = params.platformCategoryId;
    const [{ data }, roleNames] = await Promise.all([
      apiClient.get(`/users/by-platform/${platformId}`, { params: query }),
      fetchRoleNames(),
    ]);
    return z
      .array(UserSummaryDto)
      .parse(data)
      .map((dto) => toDomain(dto, roleNames));
  },
};
